import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Printer, Sparkles } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import BackButton from './BackButton';
import { useAuth } from '../context/AuthContext';

/**
 * Phase 40 · LegalPageLayout
 * ---------------------------------------------------------------
 * Reusable shell for legal/FAQ pages. Automatically switches between:
 *   - Standalone mode (visitor not logged in): brand header + language
 *     switcher + minimal footer. No AppShell chrome.
 *   - Embedded mode (already inside AppShell, e.g. /faq visited after
 *     login): no extra chrome, just the content column so it doesn't
 *     duplicate SideNav/TopBar.
 *
 * The layout is content-agnostic: pass a `sections` array (Table of
 * Contents + anchors) and children with the actual copy. Content
 * placeholders come from `t('meta.empty_section')` when a section body
 * is empty, so the page never looks broken while the copy is being
 * drafted in ChatGPT.
 * ---------------------------------------------------------------
 */

export const LegalSection = ({ id, title, body, children, testId }) => {
  const { t } = useTranslation('legal');

  // If `body` is provided, split on double-newline into paragraphs and
  // render each paragraph. Lines starting with "- " within a paragraph
  // become <li> inside a <ul>. Lines starting with "• " work the same
  // way. This lets legal.json carry structured, readable copy without
  // needing HTML in the JSON.
  const renderBody = (text) => {
    if (!text) return null;
    const blocks = String(text).split(/\n{2,}/);
    return blocks.map((block, blockIdx) => {
      // Markdown-style sub-heading (used by FAQ questions inside a section body).
      // Renders as a highlighted amber heading, visually distinct from paragraphs.
      if (block.startsWith('## ')) {
        return (
          <h3
            key={blockIdx}
            className="mt-6 mb-2 text-base font-semibold text-amber-100 md:text-[15px]"
            data-testid={`legal-subheading-${blockIdx}`}
          >
            {block.slice(3).trim()}
          </h3>
        );
      }
      const lines = block.split('\n');
      const isList = lines.every((l) => /^\s*[-•]\s+/.test(l));
      if (isList) {
        return (
          <ul key={blockIdx} className="mb-4 list-disc pl-5 space-y-1.5">
            {lines.map((l, i) => (
              <li key={i}>{l.replace(/^\s*[-•]\s+/, '')}</li>
            ))}
          </ul>
        );
      }
      return (
        <p key={blockIdx} className="mb-4 whitespace-pre-line leading-relaxed">
          {block}
        </p>
      );
    });
  };

  const hasContent = body || children;

  return (
    <section
      id={id}
      className="mb-10 scroll-mt-24"
      data-testid={testId || `legal-section-${id}`}
    >
      <h2 className="mb-3 text-xl font-semibold text-foreground">
        {title}
      </h2>
      <div className="max-w-none text-sm text-foreground/90">
        {hasContent ? (
          <>
            {body && renderBody(body)}
            {children}
          </>
        ) : (
          <p className="italic text-muted-foreground/70">{t('meta.empty_section')}</p>
        )}
      </div>
    </section>
  );
};

const LegalPageLayout = ({
  brandKind = 'legal',
  namespace = 'legal',
  eyebrow,
  title,
  subtitle,
  lastUpdated,
  sections = [],
  children,
  testId,
}) => {
  // Content strings come from the caller-provided namespace (legal or faq)
  // but chrome/meta strings (TOC, Print, Last updated, brand, back home)
  // always live in the `legal` namespace so we have one source of truth.
  const { t: tMeta, i18n } = useTranslation('legal');
  const { user } = useAuth();
  const isEmbedded = !!user;

  const formattedDate = useMemo(() => {
    if (!lastUpdated) return null;
    try {
      const d = new Date(lastUpdated);
      if (isNaN(d.getTime())) return lastUpdated;
      const locale = (i18n.language || '').startsWith('en') ? 'en-GB' : 'it-IT';
      return d.toLocaleDateString(locale, {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch { return lastUpdated; }
  }, [lastUpdated, i18n.language]);

  const content = (
    <div className="mx-auto grid w-full max-w-5xl gap-8 px-4 py-8 md:grid-cols-[220px_1fr] md:py-12">
      {/* Table of contents */}
      {sections.length > 0 && (
        <aside
          className="hidden md:block"
          aria-label={tMeta('meta.toc')}
          data-testid={`${testId || 'legal'}-toc`}
        >
          <div className="sticky top-8 rounded-2xl border border-border bg-card/50 p-4 text-sm">
            <p className="mb-3 text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              {tMeta('meta.toc')}
            </p>
            <ul className="space-y-1.5">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="block rounded-md px-2 py-1 text-xs text-muted-foreground transition hover:bg-amber-500/10 hover:text-amber-200"
                    data-testid={`legal-toc-${s.id}`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}

      {/* Content column */}
      <article
        className={sections.length === 0 ? 'md:col-span-2' : ''}
        data-testid={testId || 'legal-page'}
      >
        <header className="mb-8 border-b border-border pb-6">
          <BackButton className="mb-3" />
          {eyebrow && (
            <p className="text-[11px] uppercase tracking-[0.2em] text-amber-200/80" data-testid="legal-eyebrow">
              {eyebrow}
            </p>
          )}
          <h1 className="mt-2 text-3xl font-semibold text-foreground md:text-4xl" data-testid="legal-title">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 max-w-3xl text-sm text-muted-foreground" data-testid="legal-subtitle">
              {subtitle}
            </p>
          )}
          {/* Meta row: last updated + print + language switcher (only in standalone) */}
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span data-testid="legal-last-updated">
              {tMeta('meta.last_updated')}: <span className="font-medium text-foreground">
                {formattedDate || tMeta('meta.not_yet_published')}
              </span>
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium hover:border-amber-500/40 hover:text-amber-200"
              data-testid="legal-print-btn"
            >
              <Printer className="h-3 w-3" /> {tMeta('meta.print')}
            </button>
            {!isEmbedded && (
              <div className="ml-auto">
                <LanguageSwitcher variant="compact" testId="legal-language-switcher" />
              </div>
            )}
          </div>
        </header>

        {/* Body */}
        <div className="text-sm leading-relaxed text-foreground">
          {children}
        </div>

        {/* Footer */}
        <footer className="mt-12 border-t border-border pt-6 text-center text-xs text-muted-foreground">
          {tMeta('meta.footer_contact')}
        </footer>
      </article>
    </div>
  );

  // Standalone layout — visitor is NOT authenticated so we render the
  // brand chrome (top brand bar, back-to-home link, minimal footer).
  if (!isEmbedded) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="border-b border-border">
          <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold"
              data-testid="legal-brand-link"
            >
              <Sparkles className="h-4 w-4 gold-text" />
              <span className="text-lg gold-text">{tMeta('meta.brand', { defaultValue: 'Nymia' })}</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-amber-200"
              data-testid="legal-back-home-link"
            >
              <ArrowLeft className="h-3 w-3" /> {tMeta('meta.back_home')}
            </Link>
          </div>
        </div>
        {content}
      </div>
    );
  }

  // Embedded layout — user already authenticated, the outer AppShell
  // already provides SideNav/TopBar so we only render the content.
  return content;
};

export default LegalPageLayout;
